import { Link, Navigate } from '@tanstack/react-router'
import type { PartnerStatus, SubmissionMethod } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { Alert, Button, Card, Field, Input, Select, Textarea } from '../components/ui'
import { errorMessage } from '../lib/api'
import { formatDate, humanize } from '../lib/format'
import { useCreatePartner, usePartners } from '../lib/programs'
import { isTexStaff, useMe } from '../lib/session'

const partnerTone: Record<PartnerStatus, string> = {
  prospective: 'bg-neutral-100 text-neutral-800',
  active: 'bg-green-100 text-green-900',
  suspended: 'bg-amber-100 text-amber-900',
  terminated: 'bg-red-100 text-red-900',
}

export function PartnerStatusBadge({ status }: { status: PartnerStatus }) {
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${partnerTone[status]}`}>{humanize(status)}</span>
}

const SUBMISSION_METHODS: SubmissionMethod[] = ['manual_portal', 'mock', 'api']

export function PartnersListPage() {
  const me = useMe()
  const partners = usePartners()

  if (me.data === null) return <Navigate to="/sign-in" />
  if (me.data && !isTexStaff(me.data)) return <Alert tone="error">Only Tex staff can see funding partners.</Alert>

  return (
    <section className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-neutral-900">Funding partners</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Internal only. Partners start <em>prospective</em>; a program version can only be activated once its partner is active. See
          also{' '}
          <Link to="/tex/jurisdictions" className="underline">
            operating jurisdictions
          </Link>
          .
        </p>
      </div>

      {partners.isError && <Alert tone="error">{errorMessage(partners.error)}</Alert>}
      <div className="overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-100 bg-neutral-50/60 text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-2">Partner</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Submission</th>
              <th className="px-4 py-2 text-right">Program versions</th>
              <th className="px-4 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {partners.data?.partners.map((p) => (
              <tr key={p.id} className="border-t border-neutral-100 transition-colors hover:bg-neutral-50/60">
                <td className="px-4 py-2">
                  <Link to="/tex/partners/$partnerId" params={{ partnerId: p.id }} className="font-medium hover:underline">
                    {p.display_name}
                  </Link>
                  <div className="text-xs text-neutral-500">
                    {p.legal_name} · {p.code}
                    {p.is_synthetic && ' · synthetic'}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <PartnerStatusBadge status={p.status} />
                </td>
                <td className="px-4 py-2 text-neutral-500">{humanize(p.submission_method)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{p.program_count}</td>
                <td className="px-4 py-2 text-neutral-500">{formatDate(p.created_at)}</td>
              </tr>
            ))}
            {partners.data && partners.data.partners.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-neutral-500">
                  No partners yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {me.data?.tex_role === 'ops_admin' && <CreatePartnerCard />}
    </section>
  )
}

function CreatePartnerCard() {
  const create = useCreatePartner()
  const [code, setCode] = useState('')
  const [legalName, setLegalName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [synthetic, setSynthetic] = useState(true)
  const [method, setMethod] = useState<SubmissionMethod>('manual_portal')
  const [instructions, setInstructions] = useState('')

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate(
      {
        code: code.trim(),
        legal_name: legalName.trim(),
        display_name: displayName.trim(),
        is_synthetic: synthetic,
        submission_method: method,
        submission_instructions: instructions.trim() || null,
      },
      {
        onSuccess: () => {
          setCode('')
          setLegalName('')
          setDisplayName('')
          setInstructions('')
        },
      },
    )
  }

  return (
    <Card title="Add a partner">
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <Field label="Code" hint="Lowercase slug, stable forever.">
          <Input required pattern="[a-z0-9][a-z0-9-]{1,63}" value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label="Submission method">
          <Select value={method} onChange={(e) => setMethod(e.target.value as SubmissionMethod)}>
            {SUBMISSION_METHODS.map((m) => (
              <option key={m} value={m}>
                {humanize(m)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Legal name">
          <Input required value={legalName} onChange={(e) => setLegalName(e.target.value)} />
        </Field>
        <Field label="Display name">
          <Input required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="Submission instructions" hint="Internal; how Tex delivers a package to this partner.">
          <Textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={synthetic} onChange={(e) => setSynthetic(e.target.checked)} />
          Synthetic (fictional) partner — required for the <em>mock</em> submission method
        </label>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={create.isPending}>
            Create as prospective
          </Button>
          {create.isError && <Alert tone="error">{errorMessage(create.error)}</Alert>}
          {create.isSuccess && (
            <Alert tone="success">
              Created.{' '}
              <Link to="/tex/partners/$partnerId" params={{ partnerId: create.data.partner.id }} className="underline">
                Open {create.data.partner.display_name}
              </Link>
            </Alert>
          )}
        </div>
      </form>
    </Card>
  )
}
